import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { initials, initialsFromName } from '../../../core/functions/initials';
import { commentTime } from '../../../core/functions/relative-time';
import { AuthService } from '../../../core/services/auth/auth.service';
import { COMMENT_BODY_MAX_LENGTH, TestCaseComment } from '../../../models/test-case-comment';
import { TestCaseCommentService } from '../../../services/test-case-comment/test-case-comment.service';
import { ToastService } from '../../../ui';

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Comments on the test case being executed (PBI 57768): the thread, oldest first,
 * and a composer to add to it.
 *
 * The Execute screen's component is reused across stepper moves, so this thread
 * follows its `testCaseId` input rather than loading once: every change re-fetches,
 * and a response for a test case the tester has already left is discarded.
 */
@Component({
  selector: 'app-comment-thread',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './comment-thread.component.html',
  styleUrl: './comment-thread.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentThreadComponent {
  private commentService = inject(TestCaseCommentService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);

  readonly testCaseId = input.required<number>();

  readonly comments = signal<TestCaseComment[]>([]);
  readonly loadState = signal<LoadState>('loading');
  readonly draft = signal('');
  readonly isPosting = signal(false);
  /** Re-read once a minute so "just now" ages without a reload. */
  readonly now = signal(new Date());
  readonly maxLength = COMMENT_BODY_MAX_LENGTH;

  private readonly composer = viewChild<ElementRef<HTMLTextAreaElement>>('composer');
  /** Bumped by Retry to re-run the load for the same test case. */
  private readonly loadAttempt = signal(0);

  readonly currentUserId = computed(() => this.auth.userSig()?.userId ?? null);
  readonly myInitials = computed(() => {
    const u = this.auth.userSig();
    return initials(u?.firstName, u?.lastName);
  });
  /** Deleted comments come back flagged; how they read is the edit/delete PBI's call. */
  readonly visibleComments = computed(() => this.comments().filter((c) => !c.isDeleted));
  readonly canPost = computed(
    () => !this.isPosting() && this.draft().trim().length > 0 && this.currentUserId() != null,
  );

  constructor() {
    toObservable(computed(() => ({ testCaseId: this.testCaseId(), attempt: this.loadAttempt() })))
      .pipe(
        tap(() => {
          this.comments.set([]);
          this.loadState.set('loading');
        }),
        // switchMap drops the previous test case's request, so a slow response can
        // never paint A's thread onto B.
        switchMap(({ testCaseId }) =>
          this.commentService.getByTestCaseId(testCaseId).pipe(
            map((list) => ({ ok: true as const, list })),
            catchError(() => of({ ok: false as const, list: [] })),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ ok, list }) => {
        this.comments.set(list);
        this.loadState.set(ok ? 'ready' : 'error');
      });

    // A half-typed note belongs to the test case it was typed on.
    effect(() => {
      this.testCaseId();
      untracked(() => {
        this.draft.set('');
        this.isPosting.set(false);
      });
    });

    const ticker = setInterval(() => this.now.set(new Date()), 60000);
    inject(DestroyRef).onDestroy(() => clearInterval(ticker));
  }

  retry(): void {
    this.loadAttempt.update((n) => n + 1);
  }

  onInput(event: Event): void {
    this.draft.set((event.target as HTMLTextAreaElement).value);
  }

  post(): void {
    const userId = this.currentUserId();
    if (!this.canPost() || userId == null) return;
    const testCaseId = this.testCaseId();
    this.isPosting.set(true);

    this.commentService.create(testCaseId, { userId, body: this.draft().trim() }).subscribe({
      next: (comment) => {
        // Moved on while it saved: it is on the server and shows when they come back.
        if (this.testCaseId() !== testCaseId) return;
        this.isPosting.set(false);
        this.draft.set('');
        this.now.set(new Date());
        if (this.loadState() === 'error') this.retry();
        else this.comments.update((list) => [...list, comment]);
        this.composer()?.nativeElement.focus();
      },
      error: () => {
        if (this.testCaseId() !== testCaseId) return;
        // The draft is left in place so nothing typed is lost.
        this.isPosting.set(false);
        this.toast.error("Couldn't post your comment. Please try again.");
      },
    });
  }

  isMine(comment: TestCaseComment): boolean {
    return comment.userId === this.currentUserId();
  }

  initialsFor(comment: TestCaseComment): string {
    return initialsFromName(comment.userName);
  }

  timeLabel(comment: TestCaseComment): string {
    return commentTime(comment.createdOnUtc, this.now());
  }
}
